import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { BarChart3, Download, Paperclip, Upload } from "lucide-react";
import { formatBytes, formatCents, formatDate, formatDateTime } from "@/lib/format";
import { pendingModules } from "@/lib/stubs";
import { gatewayName, isCardless, methodLabel, serverMethodLabel } from "@/lib/payments";
import { StripeLink } from "@/components/StripeLink";
import { DisputeBadge } from "@/features/billing/PaymentBits";
import { BrandMark } from "@/components/BrandMark";
import { Empty } from "@/components/PageHeader";
import { SampleBadge } from "@/components/SampleBadge";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useBillingDetail, useFiles, useTimeline, useUploadFile } from "./hooks";

const providerLabel: Record<string, string> = { ses: "Broadcast", postmark: "Postmark", keap: "Keap (imported)", gmail: "Gmail", manual: "Logged by hand" };

/** Every email to or from this person (broadcasts, automations, Postmark, Gmail, imported Keap history). */
export function EmailHistory({ id }: { id: number }) {
  const t = useTimeline(id, ["email"]);
  const items = t.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card className="overflow-x-auto">
      {t.isLoading && <Skeleton className="m-3 h-40" />}
      {!t.isLoading && items.length === 0 && <Empty title="No email history for this person yet." />}
      {items.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Sent (MT)</TH>
              <TH>Subject</TH>
              <TH>Brand</TH>
              <TH>How</TH>
              <TH className="text-right">Opens</TH>
              <TH className="text-right">Clicks</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {items.map((it, i) => {
              const d = it.data as { opens?: number; clicks?: number; provider?: string; status?: string; report_unit_id?: number | null; direction?: string; to?: string };
              return (
                <TR key={`${it.occurred_at}-${i}`}>
                  <TD className="num whitespace-nowrap">{formatDateTime(it.occurred_at)}</TD>
                  <TD className="max-w-[28rem]">
                    <div className="truncate" title={it.summary}>
                      {d.direction === "inbound" && <span className="text-[var(--color-muted-foreground)]">Received: </span>}
                      {it.summary}
                    </div>
                    {d.to && <div className="truncate text-xs text-[var(--color-muted-foreground)]">to {d.to}</div>}
                  </TD>
                  <TD>{it.brand_id ? <BrandMark id={it.brand_id} /> : "—"}</TD>
                  <TD className="whitespace-nowrap">
                    {providerLabel[d.provider ?? ""] ?? d.provider}
                    {d.status && !["sent", "imported", "received"].includes(d.status) && (
                      <Badge variant="warning" className="ml-1">
                        {d.status}
                      </Badge>
                    )}
                  </TD>
                  <TD className="num text-right">{d.opens || "—"}</TD>
                  <TD className="num text-right">{d.clicks || "—"}</TD>
                  <TD>
                    {d.report_unit_id ? (
                      <Link to="/email/reports/$reportId" params={{ reportId: String(d.report_unit_id) }} className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline">
                        <BarChart3 className="size-3" /> Report
                      </Link>
                    ) : null}
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
      {t.hasNextPage && (
        <div className="border-t p-2">
          <Button variant="ghost" size="sm" onClick={() => t.fetchNextPage()} disabled={t.isFetchingNextPage}>
            Show older
          </Button>
        </div>
      )}
      <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
        Opens count people, not machines (Apple Mail Privacy Protection opens are left out); clicks leave out security scanners.
      </p>
    </Card>
  );
}

const categoryLabel: Record<string, string> = { attachment: "Attachment", archive_history: "Keap history (archive)", export: "Export", import: "Import", other: "Other" };

/** Attachments and the archived Keap history files. */
export function FilesView({ id }: { id: number }) {
  const files = useFiles(id);
  const upload = useUploadFile(id);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const rows = files.data?.files ?? [];
  const ready = files.data?.storage_ready ?? false;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Files &amp; archive</CardTitle>
        <div className="flex items-center gap-2">
          <input
            ref={input}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f, { onSuccess: () => toast(`Attached ${f.name}`), onError: (err) => toast(err.message, "error") });
              e.target.value = "";
            }}
          />
          <Button size="sm" variant="outline" disabled={!ready || upload.isPending} onClick={() => input.current?.click()} title={ready ? "Attach a file (up to 25 MB)" : "File storage isn't set up here"}>
            <Upload /> {upload.isPending ? "Uploading…" : "Attach a file"}
          </Button>
        </div>
      </CardHeader>
      {files.isLoading && <Skeleton className="m-3 h-24" />}
      {!files.isLoading && rows.length === 0 && (
        <Empty title="No files yet.">Attach contracts, receipts or call notes here. Archived Keap history appears here when it is loaded.</Empty>
      )}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>File</TH>
              <TH>Kind</TH>
              <TH className="text-right">Size</TH>
              <TH>Added (MT)</TH>
              <TH>By</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {rows.map((f) => (
              <TR key={f.id}>
                <TD className="max-w-[22rem]">
                  <span className="flex items-center gap-1.5 truncate">
                    <Paperclip className="size-3.5 shrink-0 text-[var(--color-muted-foreground)]" />
                    {f.filename}
                  </span>
                </TD>
                <TD>{categoryLabel[f.category] ?? f.category}</TD>
                <TD className="num text-right">{formatBytes(f.size_bytes)}</TD>
                <TD className="num whitespace-nowrap">{formatDateTime(f.created_at)}</TD>
                <TD className="truncate">{f.created_by}</TD>
                <TD>
                  {ready && (
                    <a className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline" href={`/api/v1/files/${f.id}/download`}>
                      <Download className="size-3" /> Download
                    </a>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** Orders, invoices, payments, subscriptions and saved cards (crm-billing). */
type Tone = "success" | "warning" | "destructive" | "muted" | "default";
const SUB_STATUS: Record<string, [string, Tone]> = {
  trialing: ["trial", "default"],
  active: ["active", "success"],
  past_due: ["past due", "destructive"],
  paused: ["paused", "warning"],
  cancelled: ["cancelled", "muted"],
  ended: ["ended", "muted"],
};
const ORDER_STATUS: Record<string, [string, Tone]> = {
  draft: ["draft", "muted"],
  pending: ["not paid yet", "warning"],
  paid: ["paid", "success"],
  partially_paid: ["partly paid", "warning"],
  refunded: ["refunded", "muted"],
  partially_refunded: ["partly refunded", "muted"],
  cancelled: ["cancelled", "muted"],
  void: ["void", "muted"],
};
const INVOICE_STATUS: Record<string, [string, Tone]> = {
  draft: ["draft", "muted"],
  open: ["open", "warning"],
  paid: ["paid", "success"],
  void: ["void", "muted"],
  uncollectible: ["written off", "destructive"],
  awaiting_approval: ["waiting for approval", "warning"],
};
const PAYMENT_STATUS: Record<string, [string, Tone]> = {
  pending: ["pending", "warning"],
  succeeded: ["went through", "success"],
  failed: ["declined", "destructive"],
  voided: ["voided", "muted"],
  unknown: ["unknown: check the gateway", "warning"],
};
const ORDER_SOURCE: Record<string, string> = {
  checkout: "website checkout",
  phone: "phone order",
  admin: "entered by staff",
  portal: "customer's account page",
  pay_link: "pay link",
  api: "API",
  mcp: "Claude",
  paypal: "PayPal",
  keap_import: "Keap (imported)",
};
const ACCESS_SOURCE: Record<string, string> = { subscription: "subscription", order: "purchase", trial: "free trial", comp: "complimentary", manual: "given by staff", keap_import: "Keap (imported)" };

function Chip({ map, status }: { map: Record<string, [string, Tone]>; status: string }) {
  const [label, tone] = map[status] ?? [status.replace(/_/g, " "), "muted" as Tone];
  return <Badge variant={tone}>{label}</Badge>;
}

export function BillingView({ id }: { id: number }) {
  const q = useBillingDetail(id);
  const toast = useToast();
  const d = q.data?.data;
  if (q.isLoading || !d) return <Skeleton className="h-40" />;
  const sample = q.data?.sample;
  const head = (title: string, extra?: React.ReactNode) => (
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      {extra}
      {sample && <SampleBadge stream={pendingModules.billing} />}
    </CardHeader>
  );
  const none = (cols: number, what: string) => (
    <TR>
      <TD colSpan={cols} className="text-[var(--color-muted-foreground)]">
        {what}
      </TD>
    </TR>
  );
  const copy = (url: string, what: string) => void navigator.clipboard?.writeText(url).then(() => toast(`Copied ${what}`));
  const access = d.access ?? [];
  return (
    <div className="@container space-y-3">
      <div className="grid gap-3 @xl:grid-cols-2 @3xl:grid-cols-3">
        <Card>
          {head("Subscriptions")}
          <CardContent className="space-y-2.5 text-sm">
            {d.subscriptions.length === 0 && <p className="text-[var(--color-muted-foreground)]">No subscriptions.</p>}
            {d.subscriptions.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{s.plan}</div>
                  <div className="text-xs text-[var(--color-muted-foreground)]">
                    {formatCents(s.amount_cents)} a {s.interval}, since {formatDate(s.started_on)}
                    {s.next_charge_on ? `. Next charge ${formatDate(s.next_charge_on)}` : ""}
                    {s.ends_on ? `. Ends ${formatDate(s.ends_on)}` : ""}
                  </div>
                </div>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <Chip map={SUB_STATUS} status={s.status} />
                  {s.billing_enabled === false && (
                    <Badge variant="muted" title="Imported from Keap. Keap still charges it until the final cutover switches CRM billing on.">
                      charged in Keap
                    </Badge>
                  )}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          {head("Saved cards")}
          <CardContent className="space-y-1.5 text-sm">
            {d.cards.length === 0 && <p className="text-[var(--color-muted-foreground)]">No saved card.</p>}
            {d.cards.map((c, i) => (
              <div key={c.id ?? `${c.last4}-${c.exp}-${i}`} className="flex items-start justify-between gap-3">
                <span>
                  {methodLabel(c)}
                  <span className="block text-xs text-[var(--color-muted-foreground)]">
                    {[!isCardless(c) && c.exp ? `expires ${c.exp}` : "", gatewayName(c.gateway) || c.gateway, c.default ? "default" : ""].filter(Boolean).join(", ")}
                  </span>
                  <StripeLink url={c.dashboard_url} label="Customer in Stripe" what="This customer" />
                </span>
                {c.status && c.status !== "active" && <Badge variant={c.status === "removed" ? "muted" : "destructive"}>{c.status}</Badge>}
              </div>
            ))}
            {d.stripe_customer_url && !d.cards.some((c) => c.dashboard_url) && <StripeLink url={d.stripe_customer_url} label="Customer in Stripe" what="This customer" />}
            <p className="pt-1 text-xs text-[var(--color-muted-foreground)]">Card numbers never pass through the CRM; cards are typed only into the payment gateway's own form.</p>
          </CardContent>
        </Card>
        <Card>
          {head("Access from purchases")}
          <CardContent className="space-y-1.5 text-sm">
            {access.length === 0 && <p className="text-[var(--color-muted-foreground)]">{sample ? "Shown when billing is connected." : "None right now."}</p>}
            {access.map((a) => (
              <div key={a.key} className="flex items-start justify-between gap-3">
                <span>
                  {a.key.replace(/_/g, " ")}
                  <span className="block text-xs text-[var(--color-muted-foreground)]">from {a.sources.map((x) => ACCESS_SOURCE[x] ?? x.replace(/_/g, " ")).join(", ")}</span>
                </span>
                <span className="shrink-0 text-xs text-[var(--color-muted-foreground)]">{a.until ? `until ${formatDate(a.until)}` : "no end date"}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <Card className="overflow-x-auto">
        {head("Orders")}
        <Table>
          <THead>
            <tr>
              <TH>Order</TH>
              <TH>Placed (MT)</TH>
              <TH>What</TH>
              <TH>Brand</TH>
              <TH className="text-right">Total</TH>
              <TH>Status</TH>
            </tr>
          </THead>
          <tbody>
            {d.orders.length === 0 && none(6, "No orders.")}
            {d.orders.map((o) => (
              <TR key={o.id}>
                <TD className="num">#{o.id}</TD>
                <TD className="num whitespace-nowrap">{formatDateTime(o.placed_at)}</TD>
                <TD>
                  {o.description}
                  {o.source && <div className="text-xs text-[var(--color-muted-foreground)]">{ORDER_SOURCE[o.source] ?? o.source}</div>}
                </TD>
                <TD>
                  <BrandMark id={o.brand_id} />
                </TD>
                <TD className="num text-right">{formatCents(o.total_cents)}</TD>
                <TD>
                  <Chip map={ORDER_STATUS} status={o.status} />
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>
      <div className="grid gap-3 @4xl:grid-cols-2">
        <Card className="overflow-x-auto">
          {head("Invoices")}
          <Table>
            <THead>
              <tr>
                <TH>Invoice</TH>
                <TH>Due</TH>
                <TH className="text-right">Total</TH>
                <TH className="text-right">Owed</TH>
                <TH>Status</TH>
              </tr>
            </THead>
            <tbody>
              {d.invoices.length === 0 && none(5, "No invoices.")}
              {d.invoices.map((iv, i) => (
                <TR key={iv.id ?? `${iv.number}-${i}`}>
                  <TD>
                    <span className="num">{iv.number || (iv.id ? `#${iv.id}` : "")}</span>
                    {iv.description && <div className="max-w-[16rem] truncate text-xs text-[var(--color-muted-foreground)]">{iv.description}</div>}
                    {iv.pay_url && (
                      <button
                        className="text-xs text-[var(--color-primary)] hover:underline"
                        title="The customer's link to pay this invoice. Send it to them."
                        onClick={() => copy(iv.pay_url!, "the pay link")}
                      >
                        Copy pay link
                      </button>
                    )}
                  </TD>
                  <TD className="num whitespace-nowrap">{iv.due_on ? formatDate(iv.due_on) : "—"}</TD>
                  <TD className="num text-right">{formatCents(iv.total_cents)}</TD>
                  <TD className="num text-right">{formatCents(iv.balance_cents)}</TD>
                  <TD>
                    <Chip map={INVOICE_STATUS} status={iv.status} />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card className="overflow-x-auto">
          {head("Payments")}
          <Table>
            <THead>
              <tr>
                <TH>When (MT)</TH>
                <TH className="text-right">Amount</TH>
                <TH>Paid with</TH>
                <TH>Result</TH>
              </tr>
            </THead>
            <tbody>
              {d.payments.length === 0 && none(4, "No payments.")}
              {d.payments.map((p) => (
                <TR key={p.id}>
                  <TD className="num whitespace-nowrap">{formatDateTime(p.at)}</TD>
                  <TD className="num text-right">
                    {formatCents(p.amount_cents)}
                    {!!p.refunded_cents && <div className="text-xs text-[var(--color-muted-foreground)]">{formatCents(p.refunded_cents)} refunded</div>}
                  </TD>
                  <TD>
                    {p.card_brand || p.last4 || p.wallet ? methodLabel(p) : serverMethodLabel(p.method)}
                    {p.gateway && <span className="block text-xs text-[var(--color-muted-foreground)]">via {gatewayName(p.gateway)}</span>}
                    <StripeLink url={p.dashboard_url} className="block" what="This payment" />
                  </TD>
                  <TD>
                    <span className="flex flex-wrap gap-1">
                      <Chip map={PAYMENT_STATUS} status={p.status} />
                      <DisputeBadge p={p} />
                    </span>
                    {p.sentence ? (
                      <div className="max-w-[22rem] text-xs text-[var(--color-muted-foreground)]">{p.sentence}</div>
                    ) : (
                      p.message && <div className="max-w-[16rem] text-xs text-[var(--color-muted-foreground)]">{p.message}</div>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
